// ============================================
// P0-③ 场景树右键菜单（2026-10-15）
// 项：重命名 / 显示·隐藏 / 定位 / 进入相机（漫游）/ 删除（危险项二次确认由调用方处理）
// ============================================
import { useEffect, useRef } from 'react';
import type { SceneTreeNode } from '../../utils/sceneTree';

export interface ContextMenuAction {
  key: 'rename' | 'toggleVisible' | 'locate' | 'enterCamera' | 'delete';
  label: string;
  danger?: boolean;
}

/** 按节点类型给出可用动作 */
export function actionsForNode(node: SceneTreeNode): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [];
  if (node.kind === 'object' && (node.objectType === 'model' || node.objectType === 'group' || node.objectType === 'camera' || node.objectType === 'light')) {
    actions.push({ key: 'rename', label: '重命名' });
  }
  if (node.kind === 'object' && (node.objectType === 'model' || node.objectType === 'light')) {
    actions.push({ key: 'toggleVisible', label: node.visible === false ? '显示' : '隐藏' });
  }
  if (node.kind === 'object' && node.objectType === 'model') {
    actions.push({ key: 'locate', label: '定位（聚焦）' });
  }
  if (node.kind === 'object' && node.objectType === 'camera' && node.id.startsWith('cam:path:')) {
    actions.push({ key: 'enterCamera', label: '进入相机' });
  }
  if (node.removable) {
    actions.push({ key: 'delete', label: '删除', danger: true });
  }
  return actions;
}

interface Props {
  x: number;
  y: number;
  node: SceneTreeNode;
  onAction: (action: ContextMenuAction['key'], node: SceneTreeNode) => void;
  onClose: () => void;
}

export default function SceneTreeContextMenu({ x, y, node, onAction, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const actions = actionsForNode(node);
  return (
    <div
      ref={ref}
      data-testid="scene-tree-ctx"
      style={{
        position: 'fixed', left: x, top: y, zIndex: 3000, minWidth: 132,
        background: '#1f2430', border: '1px solid #30475e', borderRadius: 6,
        boxShadow: '0 6px 20px rgba(0,0,0,0.45)', padding: '4px 0', fontSize: 13,
      }}
    >
      {actions.map((a) => (
        <div
          key={a.key}
          data-testid={`ctx-${a.key}`}
          onClick={() => { onAction(a.key, node); onClose(); }}
          style={{
            padding: '5px 12px', cursor: 'pointer',
            color: a.danger ? '#ff7875' : '#e0e0e0',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          {a.label}
        </div>
      ))}
    </div>
  );
}
